import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { pluralize } from '@prophecy/utils/string';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Dropdown } from '../Dropdown';
import { Ellipsis } from '../Ellipsis';
import { AddGemIcon, GemIcon, XCloseIcon } from '../Icons';
import { Stack } from '../Layout';
import { useMarkdownProps } from '../MarkdownEditor/context';
import { negateVariable, theme } from '../theme';
import { Text } from '../Typography';
const LinkGemButtonOverlayHeader = styled(Stack)`
  height: ${theme.sizes.x32};
  padding: 0 ${theme.spaces.x8};
`;
const ClearLinksButton = styled(Button)`
  border: ${theme.spaces.x1} solid ${theme.colors.white};

  &:hover {
    border: ${theme.spaces.x1} solid ${theme.colors.brand50};
  }
`;
const HorizontalSeparator = styled.hr`
  border: none;
  border-top: ${theme.spaces.x1} solid ${theme.borderColor.secondary};
  box-shadow: none;
  margin: 0 0 ${theme.spaces.x4} ${negateVariable(theme.spaces.x8)};
  padding: 0;
  width: calc(100% + ${theme.spaces.x16});
`;
const GemsListContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;
const TextWrapper = styled(Ellipsis)`
  max-width: 100px;
`;
export const StyledLinkGemsButton = styled(Button)`
  height: ${theme.sizes.x28};
  margin-left: ${theme.spaces.x4};
  padding: 0 ${theme.spaces.x6};
  text-wrap: nowrap;
  word-break: keep-all;
`;
export const LinkGemsButton = ({ annotationLinkGemsList, onLinkGemToAnnotation })=>{
    const { portalDropdowns = true } = useMarkdownProps();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const handleLinkGemToAnnotation = usePersistentCallback((e, gem)=>{
        e.preventDefault();
        onLinkGemToAnnotation(gem.value);
    });
    const linkedGems = useMemo(()=>{
        return annotationLinkGemsList.filter((gem)=>gem.isSelected);
    }, [
        annotationLinkGemsList
    ]);
    const isSingleGemLinked = linkedGems.length === 1;
    const noLinkedGems = linkedGems.length === 0;
    let iconComponent = null;
    let buttonContent = null;
    if (noLinkedGems) {
        buttonContent = null;
        iconComponent = /*#__PURE__*/ _jsx(AddGemIcon, {
            type: "extended"
        });
    } else if (isSingleGemLinked) {
        buttonContent = /*#__PURE__*/ _jsx(TextWrapper, {
            tooltip: true,
            children: linkedGems[0].label
        });
        iconComponent = linkedGems[0].icon;
    } else {
        buttonContent = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                linkedGems.length,
                " ",
                pluralize(linkedGems.length, 'Gem')
            ]
        });
        iconComponent = /*#__PURE__*/ _jsx(GemIcon, {
            type: "extended",
            color: theme.colors.blue600,
            size: "xxs"
        });
    }
    return /*#__PURE__*/ _jsx(Dropdown, {
        portalled: portalDropdowns,
        visible: isDropdownVisible,
        onVisibleChange: setIsDropdownVisible,
        "data-test-id": "link-gem-button-dropdown",
        overlay: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(LinkGemButtonOverlayHeader, {
                    direction: "horizontal",
                    align: "space-between",
                    alignY: "center",
                    children: [
                        /*#__PURE__*/ _jsx(Text, {
                            level: "sm",
                            tone: "secondary",
                            weight: "500",
                            children: "Link to Gem"
                        }),
                        /*#__PURE__*/ _jsxs(Stack, {
                            direction: "horizontal",
                            align: "center",
                            gap: theme.spaces.x4,
                            children: [
                                /*#__PURE__*/ _jsx(ClearLinksButton, {
                                    disablePointerFocus: true,
                                    variant: "tertiary",
                                    size: "xs",
                                    onClick: ()=>onLinkGemToAnnotation(),
                                    "data-test-id": "link-gem-button-clear-button",
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        level: "sm",
                                        weight: "medium",
                                        children: "Clear"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "plain",
                                    onClick: ()=>setIsDropdownVisible(false),
                                    icon: /*#__PURE__*/ _jsx(XCloseIcon, {
                                        type: "solid",
                                        color: theme.colors.gray600,
                                        size: "xs"
                                    }),
                                    "aria-label": "Close link gem dialog",
                                    "data-test-id": "link-gem-button-close-button"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(HorizontalSeparator, {}),
                /*#__PURE__*/ _jsx(GemsListContainer, {
                    children: annotationLinkGemsList.map((gem)=>/*#__PURE__*/ _jsx(Dropdown.Item, {
                            onSelect: (e)=>handleLinkGemToAnnotation(e, gem),
                            "data-test-id": `link-gem-button-${gem.value}-dropdown-item`,
                            children: /*#__PURE__*/ _jsxs(Stack, {
                                direction: "horizontal",
                                gap: theme.spaces.x8,
                                align: "space-between",
                                alignY: "center",
                                width: "100%",
                                children: [
                                    /*#__PURE__*/ _jsxs(Stack, {
                                        direction: "horizontal",
                                        gap: theme.spaces.x8,
                                        alignY: "center",
                                        children: [
                                            /*#__PURE__*/ _jsx(Checkbox, {
                                                checked: gem.isSelected,
                                                "aria-label": gem.label
                                            }),
                                            gem.icon,
                                            /*#__PURE__*/ _jsx(TextWrapper, {
                                                tooltip: true,
                                                children: gem.label
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TextWrapper, {
                                        tooltip: true,
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            level: "sm",
                                            tone: theme.textColor.quaternary,
                                            children: gem.component
                                        })
                                    })
                                ]
                            })
                        }, gem.value))
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx(StyledLinkGemsButton, {
            variant: "secondaryGray",
            size: "m",
            icon: iconComponent,
            iconPlacement: "left",
            tooltip: "Link Gem",
            "data-test-id": noLinkedGems ? 'link-gem-button-add-button' : 'link-gem-button-link-button',
            children: buttonContent
        })
    });
};
